import { useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import type { QueueState } from "./useDashboardSubscription";
import type { ActionState, QueueControlResult } from "./useDashboardActions";
import { IfPermission } from "../../auth/IfPermission";
import type { Permission } from "../../auth/HasPermission";
import { buildSnackbarMessage } from "./BuildSnackbarMessage";

interface QueueCardProps {
  title: string;
  queueState: QueueState;
  actionState: ActionState<QueueControlResult>;
  onPause: () => void;
  onResume: () => void;
  controlPermission: Permission;
  pauseDescription: string;
  resumeDescription: string;
  // Отсутствует у очередей, у которых нет сквозной части: карточка тогда не
  // показывает отдельную строку сквозной очереди.
  sitewideQueuedCount?: number;
}

export function QueueCard({
  title,
  queueState,
  actionState,
  onPause,
  onResume,
  controlPermission,
  pauseDescription,
  resumeDescription,
  sitewideQueuedCount,
}: QueueCardProps) {
  const [confirmAction, setConfirmAction] = useState<"pause" | "resume" | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleConfirm = () => {
    if (confirmAction === "pause") {
      onPause();
    } else if (confirmAction === "resume") {
      onResume();
    }
    setConfirmAction(null);
    setSnackbarOpen(true);
  };

  const snackbarMessage = buildSnackbarMessage(actionState);

  return (
    <Box minWidth={300} flex={1}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{title}</Typography>
            <Chip
              label={queueState.queueStatus === "active" ? "Активна" : "На паузе"}
              color={queueState.queueStatus === "active" ? "success" : "warning"}
              size="small"
              variant="outlined"
            />
          </Box>

          <Box display="flex" justifyContent="center" alignItems="stretch" gap={3} my={3}>
            <Box flex={1} textAlign="center">
              <Typography variant="h3">{queueState.articleQueuedCount}</Typography>
              <Typography variant="body2" color="text.secondary">Статьи</Typography>
            </Box>
            <Box sx={{ borderLeft: 1, borderColor: "divider" }} />
            <Box flex={1} textAlign="center">
              <Typography variant="h3">{queueState.homepageQueuedCount}</Typography>
              <Typography variant="body2" color="text.secondary">Homepage</Typography>
            </Box>
            {sitewideQueuedCount !== undefined && (
              <>
                <Box sx={{ borderLeft: 1, borderColor: "divider" }} />
                <Box flex={1} textAlign="center">
                  <Typography variant="h3">{sitewideQueuedCount}</Typography>
                  <Typography variant="body2" color="text.secondary">Sitewide</Typography>
                </Box>
              </>
            )}
          </Box>

          <IfPermission permission={controlPermission}>
            <Box display="flex" justifyContent="center">
              {queueState.queueStatus === "active" ? (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => setConfirmAction("pause")}
                  disabled={actionState.kind === "loading"}
                  startIcon={actionState.kind === "loading" ? <CircularProgress size={16} /> : undefined}
                >
                  Приостановить
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => setConfirmAction("resume")}
                  disabled={actionState.kind === "loading"}
                  startIcon={actionState.kind === "loading" ? <CircularProgress size={16} /> : undefined}
                >
                  Возобновить
                </Button>
              )}
            </Box>
          </IfPermission>
        </CardContent>

        <Dialog open={confirmAction !== null} onClose={() => setConfirmAction(null)}>
          <DialogTitle>
            {confirmAction === "pause" ? "Приостановить очередь?" : "Возобновить очередь?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmAction === "pause" ? pauseDescription : resumeDescription}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmAction(null)}>Отмена</Button>
            <Button
              onClick={handleConfirm}
              color={confirmAction === "pause" ? "warning" : "success"}
              variant="contained"
            >
              {confirmAction === "pause" ? "Приостановить" : "Возобновить"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen && snackbarMessage !== ""}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            severity={actionState.kind === "error" ? "error" : "success"}
            onClose={() => setSnackbarOpen(false)}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Card>
    </Box>
  );
}
